import { BimEntityProperties } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { ICadPropertyCategory } from 'Data/interfaces/CadViewer/ICadProperty';
import { aggregateCategories } from 'Utils/cadProperties';
import { formatQuantity } from 'Utils/bimQuantities';

// O motor IFC devolve atributos soltos na raiz da entidade, mais property sets e
// quantity sets. Aqui isso vira o formato de categorias do viewer 2D
// (`ICadPropertyCategory`) — o mesmo que o 3D usa e que `ViewerPropertiesPanel`
// sabe desenhar.
//
// Antes a tradução era para o formato da Autodesk (`IProperties`: pares
// nome/valor com unidade). Ele não tem onde guardar `aggregation`/`values`/
// `count`, então a seleção múltipla chegava ao painel sem o que a torna
// legível: "*Varia*" não abria os valores individuais e a soma não se
// identificava como soma. O formato do 2D carrega os três.

const IFC_ATTRIBUTE_LABELS: [keyof BimEntityProperties, string][] = [
  ['name', 'Nome'],
  ['ifcType', 'Tipo IFC'],
  ['predefinedType', 'Tipo predefinido'],
  ['objectType', 'Tipo do objeto'],
  ['tag', 'Tag'],
  ['description', 'Descrição'],
  ['globalId', 'GlobalId'],
];

const toAttributes = (entity: BimEntityProperties): { label: string; value: string }[] => (
  IFC_ATTRIBUTE_LABELS
    .filter(([key]) => {
      const value = entity[key];
      return value !== undefined && value !== null && value !== '';
    })
    .map(([key, label]) => ({ label, value: String(entity[key]) }))
);

/**
 * Formato intermediário do viewer 2D — é o que `aggregateCategories` sabe somar.
 * Passar por ele mesmo com UM elemento mantém um caminho só: o painel do
 * federado não pode formatar quantidade de um jeito na seleção simples e de
 * outro no total.
 */
const toCadCategories = (entity: BimEntityProperties): ICadPropertyCategory[] => {
  const attributes = toAttributes(entity);
  const categories: ICadPropertyCategory[] = [];

  if (attributes.length > 0) {
    categories.push({ categoryName: 'Identificação', properties: attributes });
  }

  // Psets e Qtos viram uma categoria cada, com o nome que veio do arquivo — é o
  // agrupamento que o usuário de IFC espera ver.
  for (const set of entity.propertySets) {
    if (set.properties.length === 0) { continue; }
    categories.push({
      categoryName: set.name,
      properties: set.properties.map((property) => ({
        label: property.name,
        value: property.value,
      })),
    });
  }

  for (const set of entity.quantitySets) {
    if (set.properties.length === 0) { continue; }
    categories.push({
      categoryName: set.name,
      properties: set.properties.map((property) => {
        const raw = Number(property.value);
        const numeric = Number.isFinite(raw);
        return {
          label: property.name,
          value: formatQuantity(property.value),
          // `rawValue` + tipo numérico é o que faz a agregação SOMAR em vez de
          // marcar "*Varia*": com vários elementos, o painel mostra o total de
          // área/volume/comprimento da seleção.
          rawValue: numeric ? raw : property.value,
          propertyType: numeric ? ('float' as const) : undefined,
        };
      }),
    });
  }

  return categories;
};

export interface BimPanelProperties {
  /** Nome do elemento; ausente quando a seleção mistura nomes diferentes. */
  name?: string;
  /** Quantos elementos a seleção tem — 1 = seleção simples, sem agregação. */
  count: number;
  categories: ICadPropertyCategory[];
}

/**
 * Seleção inteira → painel. Com mais de um elemento agrega pela mesma regra do
 * viewer 2D e do 3D: soma o que é numérico, marca "*Varia*" no que difere e
 * esconde a categoria que não existe em todos.
 */
export const bimEntitiesToProperties = (
  entities: BimEntityProperties[],
): BimPanelProperties | null => {
  if (entities.length === 0) {
    return null;
  }

  const categories = entities.length === 1
    ? toCadCategories(entities[0])
    : aggregateCategories(
      entities.map((entity) => ({
        type: entity.ifcType,
        name: entity.name,
        layer: '',
        objectId: String(entity.expressId),
        categories: toCadCategories(entity),
      })),
      // O total passa pelo mesmo arredondamento dos valores individuais; sem
      // isto a agregação devolve `String(sum)` cru e a soma aparece com o lixo
      // de ponto flutuante logo abaixo de itens já limpos.
      (sum) => formatQuantity(String(sum)),
    );

  // Um nome só quando todos coincidem. Divergindo, o cabeçalho fica sem nome e
  // quem conta a seleção é o título do painel — a linha "Nome" da Identificação
  // já mostra "*Varia*", e repetir isso no topo não acrescenta nada.
  const names = new Set(entities.map((entity) => entity.name || entity.ifcType));

  return {
    name: names.size === 1 ? entities[0].name || entities[0].ifcType : undefined,
    count: entities.length,
    categories,
  };
};
